import styled from 'styled-components';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Aside = styled.aside`
  position: sticky;
  top: calc(var(--header-height, 72px) + 1rem);
  min-width: 0;
  width: 100%;
`;

const SectionLabel = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Disclosure = styled.p`
  margin: 0 0 1.125rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.li`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.background};
`;

const CardLink = styled.a`
  display: flex;
  flex-direction: column;
  min-height: 11.5rem;
  padding: 1rem 1.05rem 1.05rem;
  text-decoration: none;
  color: inherit;
`;

const Thumb = styled.div`
  width: 100%;
  aspect-ratio: 16 / 10;
  min-height: 5.5rem;
  margin-bottom: 0.85rem;
  background: ${({ theme }) => theme.colors.secondary};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Copy = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.p`
  margin: 0 0 0.35rem;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(0.9375rem, 0.25vw + 0.875rem, 1.0625rem);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Desc = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.75rem;
  padding-top: 0.65rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const IconBtn = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.foreground};
  flex-shrink: 0;
`;

/**
 * @param {{ offers: { imageUrl?: string, title?: string, description?: string, url?: string, buttonLabel?: string }[] }} props
 */
export default function JournalOffersAside({ offers = [] }) {
  const { t } = useTranslation();
  const p = 'modamai.blogPost';
  const visible = offers.filter((o) => o?.title?.trim() && o?.url?.trim());

  if (!visible.length) return null;

  return (
    <Aside aria-labelledby="journal-offers-heading">
      <SectionLabel id="journal-offers-heading">{t(`${p}.offersTitle`)}</SectionLabel>
      <Disclosure>{t(`${p}.offersDisclosure`)}</Disclosure>
      <List>
        {visible.map((offer, index) => {
          const label = offer.buttonLabel?.trim() || t(`${p}.offersCtaDefault`);
          return (
            <Card key={`${offer.url}-${index}`}>
              <CardLink
                href={offer.url.trim()}
                target="_blank"
                rel="nofollow sponsored noopener noreferrer"
                aria-label={`${label}: ${offer.title}`}
              >
                <Thumb>{offer.imageUrl?.trim() ? <img src={offer.imageUrl} alt="" loading="lazy" /> : null}</Thumb>
                <Copy>
                  <Title>{offer.title}</Title>
                  {offer.description?.trim() ? <Desc>{offer.description}</Desc> : null}
                </Copy>
                <CardFooter>
                  <IconBtn aria-hidden>
                    <ExternalLink size={18} />
                  </IconBtn>
                </CardFooter>
              </CardLink>
            </Card>
          );
        })}
      </List>
    </Aside>
  );
}
